import type { FC } from 'react';

import AdminRolesPanel from '@/features/settings/components/AdminRolesPanel';
import CoinRulesPanel from '@/features/settings/components/CoinRulesPanel';
import FeatureTogglesPanel from '@/features/settings/components/FeatureTogglesPanel';
import SettingsForm from '@/features/settings/components/SettingsForm';
import SettingsNav from '@/features/settings/components/SettingsNav';
import { useSettingsPage } from '@/features/settings/hooks/useSettingsPage.hook';

const SettingsPage: FC = () => {
  const vm = useSettingsPage();

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
            Platform Settings
          </h1>
          <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
            Fees, feature toggles, the coin economy and admin access
          </p>
        </div>
        {vm.showSaveBar ? (
          <div className="flex items-center gap-3">
            {vm.successMessage ? (
              <span className="text-[13px] font-semibold text-[var(--color-success-ink)]">
                {vm.successMessage}
              </span>
            ) : null}
            {vm.submitError ? (
              <span className="text-[13px] text-[var(--color-danger-ink)]">{vm.submitError}</span>
            ) : null}
            <button
              type="button"
              onClick={vm.onSubmit}
              disabled={vm.isSaving}
              className="shrink-0 rounded-lg bg-brand-500 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {vm.isSaving ? 'Saving…' : '💾 Save changes'}
            </button>
          </div>
        ) : null}
      </header>

      {vm.isLoading ? (
        <div className="rounded-2xl border border-[var(--color-divider)] bg-white p-10 text-center text-[13px] text-[var(--color-ink-muted)] shadow-[var(--shadow-card)]">
          Loading settings…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[240px_1fr]">
          <SettingsNav tab={vm.tab} showAdmins={vm.isSuperAdmin} onChangeTab={vm.onChangeTab} />

          <div>
            {vm.tab === 'fees' ? (
              <SettingsForm
                form={vm.form}
                errors={vm.errors}
                isUploadingQr={vm.isUploadingQr}
                uploadError={vm.uploadError}
                onChange={vm.onChange}
                onUploadQrImage={vm.onUploadQrImage}
                onClearQrImage={vm.onClearQrImage}
              />
            ) : null}

            {vm.tab === 'flags' ? (
              <FeatureTogglesPanel flags={vm.flags} onToggle={vm.onToggleFlag} />
            ) : null}

            {vm.tab === 'coins' ? (
              <CoinRulesPanel coinRules={vm.coinRules} version={vm.coinRulesVersion} />
            ) : null}

            {vm.tab === 'admins' && vm.isSuperAdmin ? (
              <AdminRolesPanel vm={vm.admins} currentAdminId={vm.currentAdminId} />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
